import NoData from 'components/NoData';
import { useCallback } from 'react';
import { JobRoleGroupType } from 'types/jobRoles';
import { EmployeeRate, HourlyRate } from 'types/pricelists';
import { cn } from 'utils/cn';
import { getClassifierText } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers';
import { employeeRowName } from './modal/employeePricing';

interface IProps {
    data: Record<string, HourlyRate[]>;
    fontSize?: string;
    // An employee-priced list's priced people, highest price first; set, they replace the role groups.
    employeeRates?: EmployeeRate[];
}

interface DetailsRowProps {
    name: string;
    price: string;
    isLastRow?: boolean;
    fontSize?: string;
}

// `fontSize` sets the text smaller than its step. Classic keeps the step's 22px line under it; the
// new design takes the step's ratio of the smaller size.
const LINE = 'text-body1 not-new:leading-[1.375rem]';

const DetailsRow = ({ name, price, isLastRow, fontSize }: DetailsRowProps): JSX.Element => (
    <div className={cn('mt-[5px] flex justify-between pb-[3px]', !isLastRow && 'border-grey-200 border-b')}>
        <p className={cn(LINE, 'text-fg-secondary')} style={{ fontSize }}>
            {name}
        </p>
        <p className={cn(LINE, 'text-primary')} style={{ fontSize }}>
            {price}
        </p>
    </div>
);

interface JobRoleGroupProps {
    groupType: JobRoleGroupType;
    group: HourlyRate[] | undefined;
    title: string;
    fontSize?: string;
}

const JobRoleGroup = ({ groupType, group, title, fontSize }: JobRoleGroupProps): JSX.Element => (
    <>
        <p className={cn(LINE, 'text-primary font-semibold')} style={{ fontSize }}>
            {title}
        </p>
        <hr className="border-divider mt-[3px]" />
        {!group ? (
            <div className="pt-1">
                <NoData titleId="pricelists.tooltips.no-pricelist-found" />
            </div>
        ) : (
            group.length > 0 &&
            group.map((rate: HourlyRate, i: number) => {
                const isLastRow = i === group.length - 1;
                const key = `${groupType}-${i}`;
                return (
                    <DetailsRow
                        key={key}
                        name={rate.jobRole.internalName ?? rate.jobRole.name}
                        price={rate.hourlyPrice ?? ''}
                        isLastRow={isLastRow}
                        fontSize={fontSize}
                    />
                );
            })
        )}
    </>
);

// The people of an employee-priced list in one column, each as "name · role" and the price.
function EmployeeRateRows({ rates, fontSize }: { rates: EmployeeRate[]; fontSize?: string }): JSX.Element {
    return (
        <div className="p-2">
            {rates.length === 0 ? (
                <NoData titleId="pricelists.employee.no-prices" />
            ) : (
                rates.map((rate, index) => (
                    <DetailsRow
                        key={rate.employee.id}
                        name={employeeRowName(rate)}
                        price={rate.hourlyPrice ?? ''}
                        isLastRow={index === rates.length - 1}
                        fontSize={fontSize}
                    />
                ))
            )}
        </div>
    );
}

export const HourlyRateDetailsCard = ({ data, fontSize, employeeRates }: IProps): JSX.Element => {
    if (employeeRates) {
        return <EmployeeRateRows rates={employeeRates} fontSize={fontSize} />;
    }
    return <RoleRateGroups data={data} fontSize={fontSize} />;
};

function RoleRateGroups({ data, fontSize }: IProps): JSX.Element {
    const { classifiers } = useClassifiers({ codes: ['JOB_ROLE_GROUP_TYPE'] });
    const getGroupTitle = useCallback(
        (code: JobRoleGroupType) => getClassifierText(classifiers?.['JOB_ROLE_GROUP_TYPE'], code),
        [classifiers]
    );

    const legalGroup = data[JobRoleGroupType.LEGAL];
    const adminGroup = data[JobRoleGroupType.ADMIN];

    return (
        <div className="grid grid-cols-2 gap-x-8 p-2">
            <div>
                <JobRoleGroup
                    groupType={JobRoleGroupType.LEGAL}
                    group={legalGroup}
                    title={getGroupTitle(JobRoleGroupType.LEGAL)}
                    fontSize={fontSize}
                />
            </div>
            <div>
                <JobRoleGroup
                    groupType={JobRoleGroupType.ADMIN}
                    group={adminGroup}
                    title={getGroupTitle(JobRoleGroupType.ADMIN)}
                    fontSize={fontSize}
                />
            </div>
        </div>
    );
}
